<script setup>
import ResizeObserver from '@/utils/modals/resizeObserver'
import {
  stringStylesToObject,
  inRange,
  parseSizeNumber,
  validateNumber,
  modalsInjectionKey,
  windowWidthWithoutScrollbar,
} from '@/utils/modals/index'

const TransitionState = {
  Enter: 'enter',
  Entering: 'entering',
  Leave: 'leave',
  Leaving: 'leavng',
}
const defaultTransition = 'veridis-transition--mc-default'

const bus = useEventBus(modalsInjectionKey)

const emit = defineEmits(['before-open', 'opened', 'before-close', 'closed'])

const props = defineProps({
  name: { type: String, required: true },
  scrollable: { type: Boolean, default: false },
  adaptive: { type: Boolean, default: false },
  clickToClose: { type: Boolean, default: true },
  overlayTransition: {
    type: String,
    default: 'veridis-transition--mc-overlay',
  },
  transition: { type: String, default: 'veridis-transition--mc-modal' },
  classes: { type: [String, Array], default: () => [] },
  styles: { type: [String, Array, Object], default: () => [] },
  minWidth: {
    type: Number,
    default: 0,
    validator(value) {
      return value >= 0
    },
  },
  minHeight: {
    type: Number,
    default: 0,
    validator(value) {
      return value >= 0
    },
  },
  maxWidth: {
    type: Number,
    default: Number.MAX_SAFE_INTEGER,
  },
  maxHeight: {
    type: Number,
    default: Number.MAX_SAFE_INTEGER,
  },
  width: {
    type: [Number, String],
    default: 600,
    validator(value) {
      return value === 'auto' || validateNumber(value)
    },
  },
  height: {
    type: [Number, String],
    default: 300,
    validator(value) {
      return value === 'auto' || validateNumber(value)
    },
  },
  shiftX: {
    type: Number,
    default: 0.5,
    validator(value) {
      return value >= 0 && value <= 1
    },
  },
  shiftY: {
    type: Number,
    default: 0.5,
    validator(value) {
      return value >= 0 && value <= 1
    },
  },
})

const visible = ref(false)
const visibility = ref({
  modal: false,
  overlay: false,
})
const overlayTransitionState = ref(null)
const modalTransitionState = ref(null)
const modal = ref({
  width: 0,
  widthType: 'px',
  height: 0,
  heightType: 'px',
  renderedHeight: 0,
})
const veridisModal = ref(null)
const viewportHeight = ref(0)
const viewportWidth = ref(0)
const shiftLeft = ref(0)
const shiftTop = ref(0)
const resizeObserver = ref(null)

/**
 * Because modal state is based on transitions we need to make
 * sure that there's always a transition for overlay and modal
 **/
const guaranteedOverlayTransition = computed(
  () => props.overlayTransition || defaultTransition,
)
const guaranteedModalTransition = computed(
  () => props.transition || defaultTransition,
)
const isAutoHeight = computed(() => modal.value.heightType === 'auto')
const containerClass = computed(() => [
  'veridis--mc-container',
  props.scrollable && isAutoHeight.value && 'scrollable',
])
const modalClass = computed(() => ['veridis--mc-modal scrollable', props.classes])
const stylesProp = computed(() =>
  typeof props.styles === 'string'
    ? stringStylesToObject(props.styles)
    : props.styles,
)

const position = computed(() => {
  const maxLeft = viewportWidth.value - trueModalWidth.value
  const maxTop = Math.max(viewportHeight.value - trueModalHeight.value, 0)
  const left = shiftLeft.value + props.shiftX * maxLeft
  const top = shiftTop.value + props.shiftY * maxTop

  return {
    left: inRange(0, maxLeft, left),
    top:
      !trueModalHeight.value && isAutoHeight.value
        ? undefined
        : parseInt(inRange(0, maxTop, top)),
  }
})
/**
 * Returns pixel width (if set with %) and makes sure that modal size
 * fits the window
 */
const trueModalWidth = computed(() => {
  const value
    = modal.value.widthType === '%'
      ? (viewportWidth.value / 100) * modal.value.width
      : modal.value.width

  if (props.adaptive) {
    const max = Math.max(
      props.minWidth,
      Math.min(viewportWidth.value, props.maxWidth),
    )

    return inRange(props.minWidth, max, value)
  }

  return value
})
/**
 * Returns pixel height (if set with %) and makes sure that modal size
 * fits the window.
 *
 * Returns modal.renderedHeight if height set as "auto"
 */
const trueModalHeight = computed(() => {
  const value
    = modal.value.heightType === '%'
      ? (viewportHeight.value / 100) * modal.value.height
      : modal.value.height

  if (isAutoHeight.value) {
    return modal.value.renderedHeight
  }

  if (props.adaptive) {
    const max = Math.max(
      props.minHeight,
      Math.min(viewportHeight.value, props.maxHeight),
    )

    return inRange(props.minHeight, max, value)
  }

  return value
})
/**
 * Returns the height of the modal when in 'auto' mode, making
 * sure the modal fits the viewport if 'adaptive' is true
 */
const autoHeight = computed(() =>
  props.adaptive && modal.value.renderedHeight >= viewportHeight.value
    ? `${Math.max(props.minHeight, viewportHeight.value)}px`
    : 'auto',
)
const modalStyle = computed(() => [
  stylesProp.value,
  {
    top: position.value.top !== undefined ? `${position.value.top}px` : 'auto',
    left: `${position.value.left}px`,
    width: `${trueModalWidth.value}px`,
    height: isAutoHeight.value ? autoHeight.value : `${trueModalHeight.value}px`,
  },
])
const isComponentReadyToBeDestroyed = computed(
  () =>
    overlayTransitionState.value === TransitionState.Leave
    && modalTransitionState.value === TransitionState.Leave,
)

const debouncedOnWindowResize = debounce(onWindowResize, 100)

onBeforeMount(() => {
  bus.on(onModalEventHappened)

  window.addEventListener('resize', debouncedOnWindowResize)

  window.addEventListener('orientationchange', debouncedOnWindowResize)

  onWindowResize()

  /** If the escape key is clicked, hide the modal */
  if (props.clickToClose) {
    const { escape } = useMagicKeys()
    whenever(escape, () => close(props.name))
  }
  /** Making sure that autoHeight is enabled when using "scrollable" */
  if (props.scrollable && !isAutoHeight.value) {
    console.warn(
      `Modal "${props.name}" has scrollable flag set to true, but height is not "auto" (${props.height})`,
    )
  }
})

let skipObserver = false

const debouncedResizeCallback = debounce((entries) => {
  if (skipObserver) {
    /** Reset the flag and skip this callback cycle. */
    skipObserver = false
    return
  }

  if (entries.length > 0) {
    const newHeight = entries[0].contentRect.height
    const diff = newHeight - modal.value.renderedHeight

    if (Math.abs(diff) > 1) {
      /** Prevent the next callback from updating. */
      skipObserver = true
      modal.value.renderedHeight = newHeight
    }
  }
}, 100)

onMounted(() => {
  resizeObserver.value = new ResizeObserver(debouncedResizeCallback)
  nextTick(() => {
    if (veridisModal.value instanceof Element) {
      resizeObserver.value.observe(veridisModal.value)
    }
  })
})

onBeforeUnmount(() => {
  bus.off(onModalEventHappened)

  window.removeEventListener('resize', debouncedOnWindowResize)

  window.removeEventListener('orientationchange', debouncedOnWindowResize)

  /** Removes blocked scroll */
  document.body.classList.remove('veridis--mc-block-scroll')
})

onClickOutside(veridisModal, () => {
  if (props.clickToClose) {
    close(props.name)
  }
})

watch(isComponentReadyToBeDestroyed, (isReady) => {
  if (isReady) {
    visible.value = false
  }
})

setInitialSize()

function setInitialSize() {
  const { value: widthValue, type: widthType } = parseSizeNumber(props.width)
  const { value: heightValue, type: heightType } = parseSizeNumber(
    props.height,
  )

  modal.value.width = widthValue
  modal.value.height = heightValue
  modal.value.widthType = widthType
  modal.value.heightType = heightType
}
function onWindowResize() {
  viewportHeight.value = window.innerHeight
  viewportWidth.value = windowWidthWithoutScrollbar()

  ensureShiftInWindowBounds()
}
function ensureShiftInWindowBounds() {
  const maxLeft = viewportWidth.value - trueModalWidth.value
  const maxTop = Math.max(viewportHeight.value - trueModalHeight.value, 0)

  const left = shiftLeft.value + props.shiftX * maxLeft
  const top = shiftTop.value + props.shiftY * maxTop

  shiftLeft.value -= left - inRange(0, maxLeft, left)
  shiftTop.value -= top - inRange(0, maxTop, top)
}
function createModalEvent(properties = {}) {
  return {
    name: props.name,
    ref: veridisModal.value || null,
    ...properties,
  }
}
function startTransitionEnter() {
  visibility.value.overlay = true
  visibility.value.modal = true
}
function startTransitionLeave() {
  visibility.value.overlay = false
  visibility.value.modal = false
}
function beforeOverlayTransitionEnter() {
  overlayTransitionState.value = TransitionState.Entering
}
function afterOverlayTransitionEnter() {
  overlayTransitionState.value = TransitionState.Enter
}
function beforeOverlayTransitionLeave() {
  overlayTransitionState.value = TransitionState.Leaving
}
function afterOverlayTransitionLeave() {
  overlayTransitionState.value = TransitionState.Leave
}
function beforeModalTransitionEnter() {
  modalTransitionState.value = TransitionState.Entering

  nextTick(() => resizeObserver.value.observe(veridisModal.value))
}
function afterModalTransitionEnter() {
  modalTransitionState.value = TransitionState.Enter

  emit(
    'opened',
    createModalEvent({
      state: 'opened',
    }),
  )
}
function beforeModalTransitionLeave() {
  modalTransitionState.value = TransitionState.Leaving
  if (resizeObserver.value && veridisModal.value instanceof Element) {
    resizeObserver.value.unobserve(veridisModal.value)
  }
}
function afterModalTransitionLeave() {
  modalTransitionState.value = TransitionState.Leave

  emit(
    'closed',
    createModalEvent({
      state: 'closed',
    }),
  )
}
function onModalEventHappened(event, { name }) {
  if (event === 'show') {
    open(name)
  }
  if (event === 'hide') {
    close(name)
  }
}
function open(name) {
  if (name !== props.name) {
    return
  }

  if (props.scrollable) {
    document.body.classList.add('veridis--mc-block-scroll')
  }

  let cancelEvent = false

  const cancel = () => {
    cancelEvent = true
  }

  emit(
    'before-open',
    createModalEvent({
      cancel,
      state: 'before-open',
    }),
  )

  if (cancelEvent) {
    if (props.scrollable) {
      document.body.classList.remove('veridis--mc-block-scroll')
    }

    return
  }

  visible.value = true

  /* Making sure that entering tranition uses "enter" sequance instead of "appear" */
  nextTick(() => startTransitionEnter())
}
function close(name) {
  if (name !== props.name) {
    return
  }

  if (props.scrollable) {
    document.body.classList.remove('veridis--mc-block-scroll')
  }

  let cancelEvent = false

  const cancel = () => {
    cancelEvent = true
  }

  emit(
    'before-close',
    createModalEvent({
      cancel,
      state: 'before-close',
    }),
  )

  if (cancelEvent) {
    return
  }

  startTransitionLeave()
}
function onOverlayClick() {
  if (props.clickToClose) {
    close(props.name)
  }
}
</script>

<template>
  <div
    v-if="visible"
    :class="containerClass"
  >
    <transition
      :name="guaranteedOverlayTransition"
      @before-enter="beforeOverlayTransitionEnter"
      @after-enter="afterOverlayTransitionEnter"
      @before-leave="beforeOverlayTransitionLeave"
      @after-leave="afterOverlayTransitionLeave"
    >
      <div
        v-if="visibility.overlay"
        class="veridis--mc-overlay"
        :data-modal="name"
        :aria-expanded="visibility.overlay.toString()"
        @click.self.stop="onOverlayClick"
      >
        <div class="veridis--mc-top-right-slot">
          <slot name="top-right" />
        </div>
      </div>
    </transition>

    <transition
      :name="guaranteedModalTransition"
      @before-enter="beforeModalTransitionEnter"
      @after-enter="afterModalTransitionEnter"
      @before-leave="beforeModalTransitionLeave"
      @after-leave="afterModalTransitionLeave"
    >
      <div
        v-if="visibility.modal"
        ref="veridisModal"
        :class="modalClass"
        :style="modalStyle"
        :aria-expanded="visibility.modal.toString()"
        role="dialog"
        aria-modal="true"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<style>
.veridis--mc-block-scroll {
  overflow: hidden;
  width: 100vw;
}

.veridis-transition--mc-default-enter-active,
.veridis-transition--mc-default-leave-active {
  transition: all 2ms;
}

.veridis-transition--mc-overlay-enter-active,
.veridis-transition--mc-overlay-leave-active {
  transition: all 50ms;
}

.veridis-transition--mc-modal-enter-active,
.veridis-transition--mc-modal-leave-active {
  transition: all 400ms;
}

.veridis-transition--mc-default-enter,
.veridis-transition--mc-default-leave-active,
.veridis-transition--mc-overlay-enter,
.veridis-transition--mc-overlay-leave-active,
.veridis-transition--mc-modal-enter,
.veridis-transition--mc-modal-leave-active {
  opacity: 0;
}

.veridis-transition--mc-modal-enter,
.veridis-transition--mc-modal-leave-active {
  transform: translateY(-20px);
}

.veridis--mc-overlay {
  position: fixed;
  box-sizing: border-box;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.2);
  opacity: 1;
}

.veridis--mc-container {
  position: fixed;
  box-sizing: border-box;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 50;
}

.veridis--mc-modal.scrollable {
  height: 100%;
  /* min-height: 100vh; */
  overflow-y: auto;
}

.veridis--mc-modal {
  position: relative;
  box-sizing: border-box;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 20px 60px -2px rgba(27, 33, 58, 0.4);
}

.veridis--mc-top-right-slot {
  display: block;
  position: absolute;
  right: 0;
  top: 0;
}

.veridis--mc-modal.scrollable .veridis--mc-modal {
  margin-bottom: 2px;
  overflow-y: auto;
  overflow-x: hidden;
}

@media (max-width: 600px) {
  .veridis--mc-modal {
    top: 200px !important;
    left: 10px !important;
    width: calc(100% - 20px) !important;
    height: auto !important;
    max-height: calc(100% - 20px) !important;
  }
}
</style>
